/**
 * The reports' surface — the filter bar, the results table and the footer that `/reports/:model`
 * is made of, as parts rather than as a shape each page redraws from memory.
 *
 * WHY THIS EXISTS
 * ──────────────
 * `AlertInvestigationPage` is the reference implementation: a filter card above a results card,
 * an 11px uppercase header, rows you click, and a footer that states the count and the page
 * size whether or not there is a second page. Every list an operator works through should read
 * as the same list with different rows in it — and the only way that survives a second page
 * being written is for the classes to live in one place.
 *
 * These are CLASS STRINGS, not components, for the parts that vary: a table's columns are the
 * whole point of the table, and a `<DataTable columns={…}>` that tried to own them would be
 * fought by every page with a two-line cell or a merged column. The parts that DON'T vary —
 * the footer, the page-size picker, the loading rows — are components, because there is
 * nothing to vary.
 *
 * Whole literal strings, never assembled: Tailwind only emits rules for class names it can see
 * in the source, so a class built at runtime ships as no styling at all.
 */
import { BTN_GHOST, BTN_PRIMARY } from './ui';
import { PagerNav, pageWindow } from './ListPager';

/** Rows-per-page choices, and the default. The reports' ladder. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

/** The filter card above the results, and the row of controls inside it. */
export const FILTER_CARD = 'rounded-xl border border-border bg-card p-4 shadow-card';
export const FILTER_ROW = 'flex flex-wrap items-center gap-3';

/** Any control in that row — select, input, date. */
export const CONTROL = 'h-9 rounded-lg border border-border bg-input px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring';

/** The card the results sit in, and the table's own furniture. */
export const RESULTS_CARD = 'rounded-xl border border-border bg-card shadow-card overflow-hidden';
export const TABLE = 'w-full text-[13px]';
export const THEAD_ROW = 'border-b border-border bg-muted/50';
export const TH = 'whitespace-nowrap px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
export const TD = 'px-4 py-3 align-middle text-foreground';
export const ROW = 'border-b border-border transition-colors last:border-0';
export const ROW_CLICKABLE = `${ROW} cursor-pointer hover:bg-muted`;

/** The primary action in a filter bar — "Add person", "Add vehicle". The app's BTN_PRIMARY (ui.ts). */
export const PRIMARY_BUTTON = BTN_PRIMARY;

/** "Clear all" — a quiet text button, because it undoes rather than does. The app's BTN_GHOST (ui.ts). */
export const CLEAR_BUTTON = BTN_GHOST;

/** A row's icon action. `tone` is the hover colour for the destructive one. */
export const rowAction = (tone: 'default' | 'danger' = 'default') =>
  'flex h-8 w-8 items-center justify-center rounded-lg transition-colors '
  + (tone === 'danger'
    ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
    : 'text-muted-foreground hover:bg-muted hover:text-foreground');

/**
 * Rows of the right height while the real ones load.
 *
 * The reports size these to their own rows — theirs carry video frames and are 80px — because
 * skeletons of the wrong height read as the list jumping when it arrives.
 */
export function SkeletonRows({ rows = 6, height = 'h-11' }: { rows?: number; height?: string }) {
  return (
    <div className="space-y-3 p-6">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`${height} animate-pulse rounded-lg bg-muted`} />
      ))}
    </div>
  );
}

/** The reports' empty state: said in the middle of the card, not in a banner above it. */
export function EmptyRows({ children }: { children: React.ReactNode }) {
  return <div className="space-y-2 py-16 text-center text-sm text-muted-foreground">{children}</div>;
}

export interface ResultsFooterProps {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  /** What is being counted — "people", "sightings", "vehicles". */
  noun: string;
  onPage: (page: number) => void;
}

/**
 * The reports' pagination footer.
 *
 * Shown whenever there are rows, not only past page one: the count and the page size are facts
 * about the result set, and hiding them on a single page means the reader cannot tell 8 listed
 * from 8-of-80 filtered.
 */
export function ResultsFooter({ page, totalPages, total, limit, noun, onPage }: ResultsFooterProps) {
  const pages = Math.max(totalPages, 1);
  // The app's pager bar (the same markup as ListPager), with the page size kept in the count.
  return (
    <div className="flex flex-col items-center justify-between gap-2 border-t border-border px-4 py-3 sm:flex-row">
      <p className="text-[12px] text-muted-foreground">
        Page <span className="font-semibold text-foreground">{page}</span> of{' '}
        <span className="font-semibold text-foreground">{pages}</span> • {Number(total || 0).toLocaleString()} total {noun} · {limit} per page
      </p>
      {pages > 1 && <PagerNav current={page} pages={pageWindow(page, pages)} totalPages={pages} onPageChange={onPage} />}
    </div>
  );
}

/** "Show [n] per page", as the reports' filter bar carries it. */
export function PageSizePicker({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    // Not wrapped in a <label>: a label around a native select re-dispatches the click to the
    // control in some Chromium builds, which opens the dropdown and closes it again.
    <span className="ml-auto flex items-center gap-1.5 whitespace-nowrap text-[13px] text-muted-foreground">
      Show
      <select
        value={String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Rows per page"
        className="h-9 rounded-lg border border-border bg-input pl-2 pr-8 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring"
      >
        {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={String(n)}>{n}</option>)}
      </select>
      per page
    </span>
  );
}
